import { ServiceStatus } from '../../../models/PlaceService.ts';
import { useForm } from '@mantine/form';
import React, { useEffect } from 'react';
import { useTagList } from '../../../hooks/tags/useTagList.ts';
import {
  Button,
  Group,
  LoadingOverlay,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';

export interface MutateInspectionServiceHeaderInfoFormValues {
  name: string;
  serviceStatus: ServiceStatus;
  tags: string[];
}

interface MutateInspectionServiceHeaderInfoFormProps {
  initialValues: MutateInspectionServiceHeaderInfoFormValues;
  onSubmit: (formValues: MutateInspectionServiceHeaderInfoFormValues) => void;
  onValuesChange?: (
    formValues: MutateInspectionServiceHeaderInfoFormValues,
  ) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

export default function MutateInspectionServiceHeaderInfoForm({
  initialValues,
  onSubmit,
  onValuesChange,
  onCancel,
  isSubmitting,
}: MutateInspectionServiceHeaderInfoFormProps) {
  const form = useForm<MutateInspectionServiceHeaderInfoFormValues>({
    mode: 'controlled',
    initialValues: {
      name: initialValues.name,
      serviceStatus: initialValues.serviceStatus,
      tags: initialValues.tags,
    },
    validate: {
      name: (value) => {
        if (!value.trim()) return ' ';
      },
      serviceStatus: (value) => {
        if (!value) return ' ';
      },
    },
  });

  useEffect(() => {
    onValuesChange?.(form.values);
  }, [form.values, onValuesChange]);

  const { data: tags, isLoading: isTagListLoading } = useTagList();

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleClose = () => {
    form.reset();
    onCancel?.();
  };

  if (isTagListLoading) return <LoadingOverlay visible={false} />;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap={'sm'}>
          {/* 驗車服務名稱 */}
          <TextInput
            label={'驗車服務名稱'}
            placeholder={'請輸入驗車服務名稱'}
            withAsterisk
            key={form.key('name')}
            {...form.getInputProps('name')}
            styles={{
              input: {
                width: 408,
                borderColor: form.errors.name
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 服務狀態 */}
          <Select
            label={'服務狀態'}
            placeholder={'請選擇服務狀態'}
            withAsterisk
            data={[
              { label: '服務中', value: 'ONLINE' },
              { label: '暫停中', value: 'TEMPORARY_OFFLINE' },
              { label: '停止服務', value: 'DISABLED' },
            ]}
            key={form.key('serviceStatus')}
            {...form.getInputProps('serviceStatus')}
            styles={{
              input: {
                width: 408,
                borderColor: form.errors.serviceStatus
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 服務標籤 */}
          <MultiSelect
            label={
              <Group w={408} justify={'space-between'}>
                <Text size={'sm'} fw={500}>
                  服務標籤
                </Text>
                <Button
                  variant={'outline'}
                  onClick={() => window.open('/tags', '_blank')}
                  styles={{
                    root: {
                      width: 60,
                      height: 15,
                      paddingInline: 6,
                    },
                    label: {
                      fontSize: 10,
                    },
                  }}
                >
                  管理標籤
                </Button>
              </Group>
            }
            searchable
            placeholder={form.values.tags.length === 0 ? '請選擇服務標籤' : ''}
            data={tags?.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            key={form.key('tags')}
            {...form.getInputProps('tags')}
            styles={{
              input: {
                width: 408,
                borderColor: form.errors.tags
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            更新
          </Button>
        </Group>
      </form>
    </>
  );
}
